import { z } from "zod";
import { calendarDateString } from "./calendar-date.js";
// Operations dashboard + buyer-tier wire contracts (ADR-0010). Each shape
// mirrors what apps/core-api/src/routes/operations.ts emits today.
export const OperationsMode = z.enum(["prospect", "live"]);
export const OperationsBleedItem = z.object({
    id: z.string(),
    label: z.string(),
    annualCost: z.number(),
    sourceKey: z.string(),
    /** Benchmark math, not claims data; drives the dashed "modeled" chip. */
    modeled: z.literal(true),
});
export const OperationsBandCounts = z.object({
    on_track: z.number(),
    falling_behind: z.number(),
    at_risk: z.number(),
});
export const OperationsPersonaPopulation = z.object({
    personaId: z.string(),
    personaName: z.string(),
    enrolledCount: z.number(),
    bands: OperationsBandCounts,
});
export const OperationsTrajectoryPoint = z.object({
    month: z.string(),
    intervention: z.number(),
    control: z.number(),
});
export const OperationsProjectionPoint = z.object({
    month: z.string(),
    withoutHealthOS: z.number(),
    withHealthOS: z.number(),
});
/**
 * Evidence tier for a figure shown to a customer. The language a surface may
 * use is a function of this value, so it is carried on the payload rather than
 * decided in a component.
 *
 *   modeled    Published research applied to the operator's headcount and
 *              PMPM. No tenant outcome data. Future or conditional language
 *              only; never "saved", never past tense.
 *   measured   Counted from this tenant's own rows. States what happened, not
 *              what it was worth.
 *   arithmetic Measured counts multiplied by a cited unit price or
 *              coefficient. Provable line by line; asserts association, never
 *              that the product caused it.
 *   attributed Effect estimated against a control. Requires the randomised
 *              programme; nothing in the product emits this today, and the
 *              type exists so that the day something does, the language is
 *              already gated.
 */
export const OperationsCoefficientProvenance = z.object({
    personaId: z.string(),
    personaLabel: z.string(),
    populationShare: z.number(),
    pmpmAvoided: z.number(),
    sourceKey: z.string(),
    sourceFinding: z.string(),
    vintage: z.string(),
    excludes: z.string(),
    haircut: z.string(),
});
/** One worked line of the derivation, so a reader reproduces it rather than trusting it. */
export const OperationsDerivationStep = z.object({
    label: z.string(),
    expression: z.string(),
    result: z.string(),
    note: z.string().nullable(),
});
/** A claim we deliberately do not make, and the reason. Pre-empts the objection. */
export const OperationsDisclaimedClaim = z.object({
    claim: z.string(),
    why: z.string(),
    wouldRequire: z.string(),
});
/** A measured instrument: what it is, how it is scored, and its thresholds. */
export const OperationsInstrument = z.object({
    name: z.string(),
    measures: z.string(),
    formula: z.string(),
    thresholds: z.string(),
    window: z.string(),
    provenance: z.string(),
});
/** Reconciliation between the personas the product runs and the ones priced. */
export const OperationsPersonaCoverage = z.object({
    personaId: z.string(),
    label: z.string(),
    enabledInProduct: z.boolean(),
    hasCoefficient: z.boolean(),
    outcomeMetric: z.string().nullable(),
    note: z.string(),
});
export const OperationsMeasurementLedger = z.object({
    coefficients: z.array(OperationsCoefficientProvenance),
    derivation: z.array(OperationsDerivationStep),
    ceilingPmpm: z.number(),
    ceilingBasis: z.string(),
    notClaimed: z.array(OperationsDisclaimedClaim),
    cohortFloor: z.number(),
    instruments: z.array(OperationsInstrument),
    personaCoverage: z.array(OperationsPersonaCoverage),
});
export const OperationsClaimTier = z.enum(["modeled", "measured", "arithmetic", "attributed"]);
export const OperationsRoiDefaults = z.object({
    headcount: z.number(),
    pepmPrice: z.number(),
    /** Contracted PEPM band. The return is reported across it, not at a point. */
    pepmPriceLow: z.number().default(3),
    pepmPriceHigh: z.number().default(5),
    expectedAdherenceImprovement: z.number(),
    pmpmAvoidedPerParticipant: z.number(),
    year1EnrolmentShare: z.number(),
    /**
     * Share of year-one sign-ups still engaged at twelve months. A fee is charged
     * for every month; avoidance only accrues while a member is still there, so
     * omitting this assumed perfect retention and overstated every return.
     */
    year1RetentionShare: z.number().default(1),
    /**
     * How well enrolment is aimed, from 0 (a random draw from the workforce, so
     * enrollees carry the population-weighted avoidance) to 1 (seats fill from
     * the highest-avoidance condition group down).
     */
    targetingStrength: z.number().default(0),
    /**
     * The condition mix targeting draws from, healthy share included and priced
     * at zero. Served rather than reconstructed: the client used to infer the
     * healthy share as one minus the sum of the methodology page's coefficient
     * rows, which is only a partition by convention, and is absent entirely in
     * prospect mode where no methodology payload is sent.
     */
    avoidanceCohorts: z.array(z.object({ share: z.number(), pmpmAvoided: z.number() })).default([]),
    /**
     * Where each assumption came from, in one sentence, served next to the
     * number it describes. A reader asked to accept or replace a figure needs to
     * know what it rests on; a bare percentage on a slider is a request to
     * invent one.
     */
    assumptionBasis: z
        .object({ enrolment: z.string(), retention: z.string(), targeting: z.string() })
        .default({ enrolment: "", retention: "", targeting: "" }),
});
export const OperationsCitation = z.object({
    key: z.string(),
    label: z.string(),
    url: z.string(),
    year: z.number(),
});
export const OperationsTriageSeverity = z.enum(["urgent", "timely", "routine"]);
export const OperationsTriageKind = z.enum([
    "discontinuation_watch",
    "band_shift",
    "engagement_decay",
    "never_connected",
]);
export const OperationsAdherenceBand = z.enum(["on_track", "falling_behind", "at_risk"]);
export const OperationsTriageEntry = z.object({
    id: z.string(),
    severity: OperationsTriageSeverity,
    kind: OperationsTriageKind,
    personaId: z.string().nullable(),
    /** The adherence band this entry's affected members sit in, so a caller can
     *  deep-link the roster to exactly them. Null when no band applies. */
    affectedBand: OperationsAdherenceBand.nullable(),
    affectedCount: z.number(),
    headline: z.string(),
    contextLine: z.string().nullable(),
    whyNow: z.string(),
    moreContext: z.string().nullable(),
    /** Label/value pairs carrying the real underlying facts. */
    signals: z.array(z.tuple([z.string(), z.string()])),
    recommendedAction: z.string(),
    estimatedAnnualImpact: z.number(),
    deadline: z.string().nullable(),
    sourceKey: z.string(),
});
export const OperationsTriage = z.object({
    generatedAt: z.string(),
    entries: z.array(OperationsTriageEntry),
    summary: z.object({ urgent: z.number(), timely: z.number(), routine: z.number() }),
});
export const OperationsBenchmarks = z.object({
    industryMean: z.object({
        label: z.string(),
        pmpm: z.number(),
        annualTrend: z.number(),
        sourceKey: z.string(),
    }),
    topQuartile: z.object({ label: z.string(), pmpm: z.number(), sourceKey: z.string() }),
    priorYear: z.object({ pmpm: z.number(), totalCost: z.number() }).nullable(),
});
export const OperationsMethodology = z.object({
    matchingApproach: z.string(),
    matchedOn: z.array(z.string()),
    caliper: z.number(),
    exclusionCriteria: z.array(z.string()),
    baselinePeriodDays: z.number(),
    followUpPeriodDays: z.number(),
    minimumDetectableEffectPmpm: z.number(),
});
export const OperationsEngagement = z.object({
    adherence: z
        .object({ rate: z.number(), sampleSize: z.number(), windowDays: z.number() })
        .nullable(),
    attentionConsumeRate: z
        .object({
        rate: z.number(),
        consumed: z.number(),
        fired: z.number(),
        windowDays: z.number(),
    })
        .nullable(),
    deliverySuccessRate: z
        .object({
        rate: z.number(),
        delivered: z.number(),
        failed: z.number(),
        windowDays: z.number(),
    })
        .nullable(),
    averageSleepHours: z
        .object({
        hours: z.number(),
        nightCount: z.number(),
        memberCount: z.number(),
        windowDays: z.number(),
    })
        .nullable(),
    // Wellbeing check-in: the population's self-rated agreement with the five
    // Thrive behaviour statements, 1 to 5, latest reading per member. A
    // self-report engagement indicator, not a validated instrument and not a
    // claims driver: it carries no clinical threshold, so there is no at-risk
    // share to report.
    wellbeingPulse: z
        .object({
        // Mean across responders of each member's five-item mean, 1-5.
        mean: z.number(),
        // The same per behaviour, so a buyer can see which one the group rates lowest.
        behaviours: z.array(z.object({ id: z.string(), mean: z.number() })),
        memberCount: z.number(),
        // Enrolled members in scope, answered or not. The denominator.
        eligibleCount: z.number(),
        coverage: z.number(),
        windowDays: z.number(),
    })
        .nullable(),
});
// Habit formation: the leading ROI indicator that behaviour change is taking
// hold, visible weeks before claims-measured savings accrue. routineFormation
// is the share of engaged members with an established daily routine;
// stackingLift is the completion-rate advantage of habits stacked onto a real
// routine over unanchored ones (the grounded-vs-invented proof).
export const OperationsHabitFormation = z.object({
    routineFormation: z
        .object({
        membersWithRoutine: z.number(),
        activeMembers: z.number(),
        rate: z.number(),
    })
        .nullable(),
    stackingLift: z
        .object({
        stackedSteps: z.number(),
        stackedCompletionRate: z.number(),
        unanchoredSteps: z.number(),
        unanchoredCompletionRate: z.number(),
        relativeLift: z.number().nullable(),
        windowDays: z.number(),
    })
        .nullable(),
});
// Whether a push still lands once the novelty has gone. Bucketed by weeks since
// a member's first push, because a pooled average hides habituation completely.
export const OperationsPushHabituation = z.object({
    weeks: z.array(z.object({
        weekIndex: z.number(),
        delivered: z.number(),
        responded: z.number(),
        rate: z.number(),
        members: z.number(),
    })),
    responseWindowHours: z.number(),
    weeklyChange: z.number().nullable(),
});
/**
 * `milestone` and `comms` exist because the three contract dates were never
 * the whole account plan: a rollout has a kickoff, a benefits fair, a comms
 * drop, an exec readout. Anything a CSM adds by hand lands in one of those two
 * unless it genuinely is a check-in, review or renewal.
 */
export const OpsCalendarKind = z.enum(["check_in", "qbr", "renewal", "milestone", "comms"]);
export const OpsCalendarEvent = z.object({
    id: z.string(),
    date: z.string(),
    end: z.string().nullable(),
    kind: OpsCalendarKind,
    title: z.string(),
    description: z.string(),
    /** True for the rows seeded from the programme start. Editable either way. */
    derived: z.boolean().default(false),
});
export const OperationsKeyDates = z.object({
    derivedFrom: z.string().nullable(),
    events: z.array(OpsCalendarEvent),
});
const isoDate = calendarDateString("date");
export const OperationsCalendarEventUpsertRequest = z.object({
    date: isoDate,
    end: isoDate.nullable().optional(),
    kind: OpsCalendarKind,
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).default(""),
});
export const OperationsCalendarEventResponse = z.object({ event: OpsCalendarEvent });
export const OperationsCalendarDeleteResponse = z.object({ ok: z.literal(true), id: z.string() });
export const OperationsBaseline = z.object({
    /**
     * Where the modeled figures' headcount and PMPM came from.
     *
     *   company   one tenant's own saved baseline.
     *   portfolio every tenant rolled up, for a caller viewing all companies.
     *             Headcount sums; PMPM is weighted by headcount.
     *   default   nobody has entered one, so these are industry benchmarks and
     *             the surface must say so.
     */
    source: z.enum(["company", "portfolio", "default"]),
    headcount: z.number(),
    currentPmpm: z.number(),
    /** Operator-entered baselines behind the figure. Above one it is a roll-up. */
    companies: z.number().default(1),
    updatedAt: z.string().nullable(),
});
export const OperationsBriefBodySegment = z.object({
    text: z.string(),
    emphasis: z.boolean().optional(),
});
export const OperationsBriefSection = z.object({
    overline: z.string(),
    title: z.string(),
    body: z.string(),
});
/** Template-composed ops brief (lib/brief-composers.ts composeOpsBrief). */
export const OperationsBriefCopy = z.object({
    eyebrow: z.string(),
    date: z.string(),
    headline: z.string(),
    subtitle: z.string(),
    durationLabel: z.string(),
    body: z.array(OperationsBriefBodySegment),
    sections: z.array(OperationsBriefSection),
    sources: z.array(z.string()),
    audioUrl: z.string(),
    isPlaceholder: z.boolean(),
});
export const OperationsDashboardResponse = z.object({
    mode: OperationsMode,
    generatedAt: z.string(),
    bleed: z.object({
        totalAnnualBleed: z.number(),
        headcount: z.number(),
        breakdown: z.array(OperationsBleedItem),
    }),
    population: z.object({
        headcount: z.number(),
        enrolledCount: z.number(),
        perPersona: z.array(OperationsPersonaPopulation),
    }),
    trajectory: z.array(OperationsTrajectoryPoint),
    projection: z.array(OperationsProjectionPoint),
    roiDefaults: OperationsRoiDefaults,
    sources: z.array(OperationsCitation),
    triage: OperationsTriage,
    benchmarks: OperationsBenchmarks,
    /** Null until a matched-cohort study has actually run for the tenant. */
    methodology: OperationsMethodology.nullable(),
    engagement: OperationsEngagement,
    habitFormation: OperationsHabitFormation,
    pushHabituation: OperationsPushHabituation,
    keyDates: OperationsKeyDates,
    /** Live mode only; prospect responses omit it. */
    baseline: OperationsBaseline.optional(),
    measurement: OperationsMeasurementLedger.optional(),
    brief: OperationsBriefCopy,
});
export const OperationsQueueDismissResponse = z.object({
    ok: z.literal(true),
    entryId: z.string(),
    dismissedAt: z.string(),
    hiddenForDays: z.number(),
});
export const OperationsMeResponse = z.object({
    userId: z.string(),
    roles: z.array(z.string()),
    capabilities: z.array(z.string()),
    companyId: z.string().nullable(),
    allCompanies: z.boolean(),
    /** Cross-tenant writes, such as a broadcast to everyone on HealthOS. */
    superAdmin: z.boolean().optional(),
    canAccessOperations: z.literal(true),
});
export const OperationsCompanySummary = z.object({
    id: z.string(),
    name: z.string().nullable(),
    members: z.number(),
    onboarded: z.number(),
});
export const OperationsCompaniesResponse = z.object({
    companies: z.array(OperationsCompanySummary),
});
/**
 * The persisted scenario payload is spread flat onto the row, so every
 * numeric field is present only when the save recorded it.
 */
export const OperationsSavedScenario = z.object({
    id: z.string(),
    name: z.string(),
    createdBy: z.string().optional(),
    createdAt: z.string(),
    headcount: z.number().optional(),
    pepmPrice: z.number().optional(),
    year1EnrolmentShare: z.number().optional(),
    /**
     * Retention and targeting move the answer more than anything else on the
     * form, so a snapshot without them cannot be reproduced: two rows with the
     * same headcount, price and sign-up could carry different savings and a
     * reader would have no way to see why.
     */
    year1RetentionShare: z.number().optional(),
    targetingStrength: z.number().optional(),
    pmpmAvoidedPerParticipant: z.number().optional(),
    expectedAdherenceImprovement: z.number().optional(),
    netAnnualSavings: z.number().optional(),
    roiMultiple: z.number().optional(),
    paybackMonths: z.number().optional(),
    threeYearNpv: z.number().optional(),
});
/**
 * What a saver may persist: the numeric scenario fields only. The row
 * envelope (id, name, createdBy, createdAt) is server-assigned and can
 * never be spoofed through the payload.
 */
export const OperationsScenarioPayload = OperationsSavedScenario.omit({
    id: true,
    name: true,
    createdBy: true,
    createdAt: true,
});
export const OperationsScenariosResponse = z.object({
    scenarios: z.array(OperationsSavedScenario),
});
export const OperationsScenarioCreateResponse = z.object({
    scenario: OperationsSavedScenario,
});
/** Company-switcher narrowing for global callers on non-GET ops routes. */
export const OperationsCompanyScopedQuery = z.object({
    companyId: z.string().optional(),
});
export const OperationsScenarioDeleteResponse = z.object({
    deleted: z.string(),
});
export const OperationsPersonRow = z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    joinedAt: z.string(),
    onboarded: z.boolean(),
    onboardedAt: z.string().nullable(),
    connected: z.boolean(),
});
export const OperationsPeopleFunnel = z.object({
    headcount: z.number().nullable(),
    accounts: z.number(),
    onboarded: z.number(),
    connected: z.number(),
});
export const OperationsPeopleResponse = z.object({
    generatedAt: z.string(),
    funnel: OperationsPeopleFunnel,
    people: z.array(OperationsPersonRow),
});
export const OperationsPersonIdentityParams = z.object({
    id: z.string().uuid(),
});
export const OperationsPersonCohort = z.object({
    id: z.string(),
    name: z.string(),
});
/**
 * Roles and cohort/segment memberships sourced live from Keycloak/Identity
 * through the federated gateway (read:identity scope). `available: false`
 * means the gateway is unconfigured or unreachable, not that the person has
 * no roles/cohorts; the UI must render an honest "unavailable" state rather
 * than an empty list in that case.
 */
export const OperationsPersonIdentityResponse = z.object({
    userId: z.string(),
    available: z.boolean(),
    roles: z.array(z.string()),
    cohorts: z.array(OperationsPersonCohort),
});
/**
 * `reason` is required (not just recommended): this flips real login
 * access on or off for a real person, and the reason is what makes the
 * audit trail actually useful later.
 */
export const OperationsSetPersonEnabledRequest = z.object({
    enabled: z.boolean(),
    reason: z.string().trim().min(1).max(500),
});
export const OperationsSetPersonEnabledResponse = z.object({
    userId: z.string(),
    enabled: z.boolean(),
});
// The Keycloak realm roles an ops admin may assign/unassign for a
// person. They stack independently. "user:healthos" (the API ticket) is
// deliberately excluded: it is never editable through this endpoint. An
// empty `roles` array means none of these extras is assigned (they still
// keep the fixed "user:healthos" baseline).
export const OperationsEditableRole = z.enum([
    "user:healthos:base",
    "user:healthos:coach",
    "user:healthos:contact-centre",
    "user:healthos:customer-admin",
]);
/** `reason` is required for the same audit reason as OperationsSetPersonEnabledRequest. */
export const OperationsSetPersonRoleRequest = z.object({
    roles: z.array(OperationsEditableRole),
    reason: z.string().trim().min(1).max(500),
});
export const OperationsSetPersonRoleResponse = z.object({
    userId: z.string(),
    roles: z.array(OperationsEditableRole),
});
export const OperationsSignalKind = z.enum([
    "attention_fired",
    "attention_consumed",
    "delivery_succeeded",
    "delivery_failed",
    "consent_granted",
    "consent_revoked",
    "consent_violation",
]);
export const OperationsSignalSeverity = z.enum(["urgent", "timely", "routine", "info"]);
export const OperationsSignalRow = z.object({
    id: z.string(),
    occurredAt: z.string(),
    kind: OperationsSignalKind,
    severity: OperationsSignalSeverity,
    memberRef: z.string().nullable(),
    source: z.string(),
    summary: z.string(),
});
/** Event-stream filters; kind is a comma-separated OperationsSignalKind list. */
export const OperationsSignalsQuery = z.object({
    companyId: z.string().optional(),
    kind: z.string().optional(),
    severity: z.string().optional(),
    limit: z.string().optional(),
});
export const OperationsSignalsResponse = z.object({
    generatedAt: z.string(),
    windowSinceIso: z.string(),
    total: z.number(),
    facets: z.object({
        byKind: z.record(OperationsSignalKind, z.number()),
        bySeverity: z.record(OperationsSignalSeverity, z.number()),
    }),
    rows: z.array(OperationsSignalRow),
});
export const OperationsInvoice = z.object({
    id: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    invoicedHeadcount: z.number(),
    pepmPrice: z.number(),
    amount: z.number(),
    status: z.enum(["paid", "open", "projected"]),
    issuance: z.enum(["computed", "invoiced"]),
});
export const OperationsBillingResponse = z.object({
    generatedAt: z.string(),
    /** Null until an org contract record exists; pricing is never fabricated. */
    contract: z
        .object({
        pepmPrice: z.number(),
        source: z.enum(["platform_default", "org_contract"]),
        termMonths: z.number(),
        startDate: z.string(),
        renewalDate: z.string(),
        daysToRenewal: z.number(),
        autoRenew: z.boolean(),
        termsNote: z.string(),
    })
        .nullable(),
    currentMonth: z.object({
        enrolledHeadcount: z.number(),
        /** Null without a contract: there is no real rate to multiply by. */
        monthlyRunRate: z.number().nullable(),
        annualRunRate: z.number().nullable(),
    }),
    /** Null until the billing ledger ships. */
    ytd: z
        .object({
        monthsElapsed: z.number(),
        spend: z.number(),
        averageMonthlyHeadcount: z.number(),
    })
        .nullable(),
    /** Null until a real invoice exists to reconcile against. */
    reconciliation: z
        .object({
        enrolledHeadcount: z.number(),
        invoicedHeadcountThisMonth: z.number(),
        deltaThisMonth: z.number(),
    })
        .nullable(),
    invoices: z.array(OperationsInvoice),
    remitNote: z.string(),
});
export const OperationsCompanyProfile = z.object({
    headcount: z.number(),
    currentPmpm: z.number(),
    /** Contracted price in dollars. Null means the published band is shown. */
    contractedPepm: z.number().nullable(),
    /**
     * The day the programme starts, as agreed with the customer. Null falls back
     * to the first completed onboarding, which is an accident of who signed up
     * first rather than a date anyone chose.
     */
    pilotStart: z.string().nullable(),
    updatedAt: z.string(),
});
export const OperationsCompanyProfileResponse = z.object({
    profile: OperationsCompanyProfile.nullable(),
});
export const OperationsCompanyProfileUpdateResponse = z.object({
    ok: z.literal(true),
    headcount: z.number(),
    currentPmpm: z.number(),
});
export const OperationsCompanyProfileUpdateRequest = z.object({
    headcount: z.number().int().min(1).max(1_000_000),
    currentPmpm: z.number().int().min(1).max(5000),
    /**
     * Dollars, stored as cents. Optional: an operator who has not been told the
     * contracted price should not be forced to invent one, and null keeps the
     * published band. Bounded well above the $3-5 band so a genuine enterprise
     * price is not rejected.
     */
    contractedPepm: z.number().min(0.01).max(500).nullable().optional(),
    /** ISO date only, no time. Null clears it back to the onboarding fallback. */
    pilotStart: calendarDateString("programme start").nullable().optional(),
});
export const OperationsSiteCluster = z.object({
    lat: z.number(),
    lon: z.number(),
    members: z.number(),
    bands: OperationsBandCounts,
});
export const OperationsSitesResponse = z.object({
    generatedAt: z.string(),
    sites: z.array(OperationsSiteCluster),
});
export const OrgIntegrationState = z.enum([
    "live",
    "available",
    "soon",
    "setup",
    "paused",
    "attention",
]);
export const OrgIntegrationTile = z.object({
    /** The manifest slug: the one integration key end to end. */
    id: z.string(),
    name: z.string(),
    kind: z.string(),
    state: OrgIntegrationState,
    desc: z.string(),
    scopes: z.array(z.string()),
    foot: z.string(),
    membersReachable: z.number().nullable(),
    brandAsset: z.string().optional(),
    brandMonogram: z.string(),
    brandKey: z.enum(["okta", "thrive"]).nullable(),
    action: z.object({ href: z.string(), label: z.string(), external: z.boolean() }).nullable(),
});
export const OperationsOrgIntegrationsResponse = z.object({
    generatedAt: z.string(),
    integrations: z.array(OrgIntegrationTile),
    /**
     * Whether the Microsoft Teams Outlook Calendar connector may be offered
     * for the caller's (or selected) company. Default off in ConfigCat: the
     * integration isn't production-ready, so the frontend keeps the ms-teams
     * tile in its catalog "on the way" state with no manage controls until
     * this flips true for a tenant.
     */
    msTeamsCalendarIntegrationEnabled: z.boolean(),
});
//# sourceMappingURL=operations.js.map